import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Accounting from "accounting";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { classNames } from "primereact/utils";
import {
  hasAnyReportColumns,
  ownerContributionPlanGroupsOverlap,
} from "../../constants/reportConfiguration";
import "../../Styles/pages/planCreate.scss";
import "../../Styles/components/sti.css";
import "../../Styles/reportConfigs.scss";
import AutoBillingBadge from "../../Components/AutoBillingBadge";

const formatPlanCost = (plan) => {
  if (plan?.cost == null || plan?.cost === "") return null;
  const amount = Number(plan.cost);
  if (Number.isNaN(amount)) return null;
  const currency = plan.business?.currency;
  if (!currency) {
    return Accounting.formatMoney(amount, { precision: 2 });
  }
  return Accounting.formatMoney(amount, {
    symbol: currency,
    format: "%s %v",
    thousand: ",",
    decimal: ".",
    precision: 2,
  });
};

const getPlanDetailsLabel = (plan) => {
  const parts = [];
  if (plan?.name) parts.push(plan.name);
  if (plan?.billingPeriod) parts.push(plan.billingPeriod);
  const costLabel = formatPlanCost(plan);
  if (costLabel) parts.push(costLabel);
  return parts.join(" - ");
};

const getPlanChipLabel = (plan) => {
  const details = getPlanDetailsLabel(plan);
  if (!details) return plan?.name || "";
  return plan?.groupName ? `${plan.groupName} — ${details}` : details;
};

const enrichPlanOption = (plan) => ({
  ...plan,
  label: getPlanDetailsLabel(plan) || plan?.name || String(plan.id),
});

const enrichSelectedPlanOption = (plan) => ({
  ...enrichPlanOption(plan),
  label: getPlanChipLabel(plan) || enrichPlanOption(plan).label,
});

const sortPlansByGroup = (plans) =>
  [...plans].sort((a, b) => {
    const groupCompare = (a.groupName || "").localeCompare(b.groupName || "");
    if (groupCompare !== 0) return groupCompare;
    return (a.name || "").localeCompare(b.name || "");
  });

function CreateReportConfigModal({
  visible,
  onHide,
  form,
  onFormChange,
  segments,
  tagGroups,
  plans,
  planGroups = [],
  fetchingOptions,
  onSubmit,
  creating,
  submitAttempted,
  submitError,
}) {
  const { t } = useTranslation("common");

  const nameInvalid = submitAttempted && !String(form.name || "").trim();
  const segmentInvalid = submitAttempted && !form.segmentId;
  const tagGroupsAreSame =
    !!form.sheetTagGroupId &&
    !!form.sectionTagGroupId &&
    String(form.sheetTagGroupId) === String(form.sectionTagGroupId);

  const sheetTagGroupOptions = useMemo(
    () =>
      tagGroups.filter(
        (group) => String(group.id) !== String(form.sectionTagGroupId || "")
      ),
    [tagGroups, form.sectionTagGroupId]
  );

  const sectionTagGroupOptions = useMemo(
    () =>
      tagGroups.filter(
        (group) => String(group.id) !== String(form.sheetTagGroupId || "")
      ),
    [tagGroups, form.sheetTagGroupId]
  );

  const columnsInvalid = submitAttempted && !hasAnyReportColumns(form);
  const planGroupsOverlapInvalid =
    submitAttempted &&
    ownerContributionPlanGroupsOverlap(
      form.ownerPlanGroupIds,
      form.contributionPlanGroupIds
    );

  const handleSheetTagGroupChange = useCallback(
    (sheetTagGroupId) => {
      if (
        form.sectionTagGroupId &&
        String(sheetTagGroupId) === String(form.sectionTagGroupId)
      ) {
        onFormChange({ sheetTagGroupId, sectionTagGroupId: "" });
        return;
      }
      onFormChange({ sheetTagGroupId });
    },
    [form.sectionTagGroupId, onFormChange]
  );

  const handleSectionTagGroupChange = useCallback(
    (sectionTagGroupId) => {
      if (
        form.sheetTagGroupId &&
        String(sectionTagGroupId) === String(form.sheetTagGroupId)
      ) {
        onFormChange({ sectionTagGroupId, sheetTagGroupId: "" });
        return;
      }
      onFormChange({ sectionTagGroupId });
    },
    [form.sheetTagGroupId, onFormChange]
  );

  const segmentDisabled = fetchingOptions || !segments.length;
  const tagGroupDisabled = fetchingOptions || !tagGroups.length;

  const segmentPlaceholder = fetchingOptions
    ? t("common.loading")
    : segments.length
      ? t("reportConfigs.choose_segment")
      : t("common.no_data");

  const sheetTagPlaceholder = fetchingOptions
    ? t("common.loading")
    : tagGroups.length
      ? t("reportConfigs.choose_sheet_tag_group")
      : t("common.no_data");

  const sectionTagPlaceholder = fetchingOptions
    ? t("common.loading")
    : tagGroups.length
      ? t("reportConfigs.choose_section_tag_group")
      : t("common.no_data");

  const sortedPlans = useMemo(() => sortPlansByGroup(plans), [plans]);

  const groupedPlanOptions = useMemo(() => {
    const groups = new Map();
    sortedPlans.forEach((plan) => {
      const label = plan.groupName || t("products.title");
      if (!groups.has(label)) {
        groups.set(label, []);
      }
      groups.get(label).push(enrichPlanOption(plan));
    });
    return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
  }, [sortedPlans, t]);

  const selectedPlans = useMemo(
    () =>
      sortedPlans
        .filter((plan) =>
          (form.planIds || []).some((id) => String(id) === String(plan.id))
        )
        .map(enrichSelectedPlanOption),
    [sortedPlans, form.planIds]
  );

  const sortedPlanGroups = useMemo(
    () => [...planGroups].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [planGroups]
  );

  const ownerPlanGroupOptions = useMemo(
    () =>
      sortedPlanGroups.filter(
        (group) =>
          !(form.contributionPlanGroupIds || []).some(
            (id) => String(id) === String(group.id)
          )
      ),
    [sortedPlanGroups, form.contributionPlanGroupIds]
  );

  const contributionPlanGroupOptions = useMemo(
    () =>
      sortedPlanGroups.filter(
        (group) =>
          !(form.ownerPlanGroupIds || []).some((id) => String(id) === String(group.id))
      ),
    [sortedPlanGroups, form.ownerPlanGroupIds]
  );

  const selectedOwnerPlanGroups = useMemo(
    () =>
      sortedPlanGroups.filter((group) =>
        (form.ownerPlanGroupIds || []).some((id) => String(id) === String(group.id))
      ),
    [sortedPlanGroups, form.ownerPlanGroupIds]
  );

  const selectedContributionPlanGroups = useMemo(
    () =>
      sortedPlanGroups.filter((group) =>
        (form.contributionPlanGroupIds || []).some(
          (id) => String(id) === String(group.id)
        )
      ),
    [sortedPlanGroups, form.contributionPlanGroupIds]
  );

  const footer = (
    <div className="report-config-modal__footer">
      <Button
        label={t("common.cancel")}
        icon="pi pi-times"
        className="report-config-modal__btn-cancel p-button-text"
        onClick={onHide}
        disabled={creating}
      />
      <Button
        label={creating ? t("reportConfigs.creating") : t("common.create")}
        className="report-config-modal__btn-create"
        onClick={onSubmit}
        disabled={creating || fetchingOptions}
        loading={creating}
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={t("reportConfigs.create_title")}
      className="report-config-modal"
      style={{ width: "720px", maxWidth: "95vw" }}
      footer={footer}
      modal
      dismissableMask={!creating}
      closable={!creating}
    >
      <form
        className="p-fluid wizard-forms"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <div className="form_div report-config-modal__form">
          <p className="hints report-config-modal__intro">{t("reportConfigs.create_intro")}</p>

          <section className="report-config-modal__section">
            <span className="plan-title">{t("reportConfigs.section_basics")}</span>
            <p className="first_hr" />

            <div className="p-field">
            <label htmlFor="report-config-name">
              <span className="labels">{t("common.name")}</span>
              <span className="isterik">*</span>
            </label>
            <div className="inputFields">
              <InputText
                id="report-config-name"
                className={classNames({ "p-invalid": nameInvalid })}
                placeholder={t("reportConfigs.name_placeholder")}
                value={form.name}
                onChange={(e) => onFormChange({ name: e.target.value })}
              />
            </div>
            {nameInvalid && (
              <small className="p-error">{t("reportConfigs.name_required")}</small>
            )}
          </div>

          <div className="p-field">
            <label htmlFor="report-config-segment">
              <span className="labels">{t("reportConfigs.segment")}</span>
              <span className="isterik">*</span>
            </label>
            <div className="inputFields">
              <Dropdown
                id="report-config-segment"
                value={form.segmentId || null}
                options={segments}
                optionLabel="name"
                optionValue="id"
                placeholder={segmentPlaceholder}
                disabled={segmentDisabled}
                showClear
                className={classNames({ "p-invalid": segmentInvalid })}
                onChange={(e) => onFormChange({ segmentId: e.value ?? "" })}
              />
            </div>
            {segmentInvalid && (
              <small className="p-error">{t("reportConfigs.segment_required")}</small>
            )}
          </div>
          </section>

          <section className="report-config-modal__section report-config-modal__section--tag-groups">
            <span className="plan-title">{t("reportConfigs.section_tag_groups")}</span>
            <p className="first_hr" />

            <div className="grid report-config-modal__tag-groups justify-content-start">
              <div className="col-12 md:col-6 p-field">
              <label htmlFor="report-config-sheet-tag-group">
                <span className="labels">{t("reportConfigs.sheet_tag_group")}</span>
              </label>
              <div className="inputFields">
                <Dropdown
                  id="report-config-sheet-tag-group"
                  value={form.sheetTagGroupId || null}
                  options={sheetTagGroupOptions}
                  optionLabel="name"
                  optionValue="id"
                  placeholder={sheetTagPlaceholder}
                  disabled={tagGroupDisabled}
                  showClear
                  className={classNames({ "p-invalid": tagGroupsAreSame })}
                  onChange={(e) => handleSheetTagGroupChange(e.value || "")}
                />
              </div>
              {tagGroupsAreSame && (
                <small className="p-error">{t("reportConfigs.tag_groups_must_differ")}</small>
              )}
              </div>
              <div className="col-12 md:col-6 p-field">
              <label htmlFor="report-config-section-tag-group">
                <span className="labels">{t("reportConfigs.section_tag_group")}</span>
              </label>
              <div className="inputFields">
                <Dropdown
                  id="report-config-section-tag-group"
                  value={form.sectionTagGroupId || null}
                  options={sectionTagGroupOptions}
                  optionLabel="name"
                  optionValue="id"
                  placeholder={sectionTagPlaceholder}
                  disabled={tagGroupDisabled}
                  showClear
                  className={classNames({ "p-invalid": tagGroupsAreSame })}
                  onChange={(e) => handleSectionTagGroupChange(e.value || "")}
                />
              </div>
              </div>
            </div>
          </section>

          <section className="report-config-modal__section report-config-modal__section--columns">
            <span className="plan-title">{t("reportConfigs.section_columns")}</span>
            <p className="first_hr" />
            <p className="hints report-config-modal__section-hint">{t("reportConfigs.columns_hint")}</p>

          {planGroupsOverlapInvalid && (
            <Message
              severity="error"
              text={t("reportConfigs.plan_groups_overlap")}
              className="w-full mb-3"
            />
          )}

          <div className="p-field report-config-modal__column-field">
            <label htmlFor="report-config-plans" className="report-config-modal__column-label">
              <span className="labels">{t("report.product_plan")}</span>
            </label>
            <span className="report-config-modal__column-control">
              <MultiSelect
                id="report-config-plans"
                value={selectedPlans}
                options={groupedPlanOptions}
                optionLabel="label"
                dataKey="id"
                optionGroupLabel="label"
                optionGroupChildren="items"
                placeholder={t("report.product_plan_hint")}
                filter
                showClear
                display="chip"
                itemTemplate={(plan) => (
                  <span className="flex align-items-center gap-2">
                    <span>{plan.label}</span>
                    <AutoBillingBadge collectionMode={plan.collectionMode} />
                  </span>
                )}
                disabled={fetchingOptions || !plans.length}
                panelClassName="report-config-multiselect-panel"
                className={classNames("p-column-filter report-config-column-field", {
                  "p-invalid": columnsInvalid,
                })}
                onChange={(e) =>
                  onFormChange({ planIds: (e.value || []).map((plan) => plan.id) })
                }
              />
            </span>
          </div>

          <div className="p-field report-config-modal__column-field">
            <label htmlFor="report-config-owner-plan-groups" className="report-config-modal__column-label">
              <span className="labels">{t("reportConfigs.column_owner_plan_groups")}</span>
            </label>
            <span className="report-config-modal__column-control">
              <MultiSelect
                id="report-config-owner-plan-groups"
                value={selectedOwnerPlanGroups}
                options={ownerPlanGroupOptions}
                optionLabel="name"
                dataKey="id"
                placeholder={t("reportConfigs.filter_plan_groups_placeholder")}
                filter
                showClear
                display="chip"
                disabled={fetchingOptions || !planGroups.length}
                panelClassName="report-config-multiselect-panel"
                className={classNames("p-column-filter report-config-column-field", {
                  "p-invalid": columnsInvalid || planGroupsOverlapInvalid,
                })}
                onChange={(e) =>
                  onFormChange({
                    ownerPlanGroupIds: (e.value || []).map((group) => group.id),
                  })
                }
              />
            </span>
          </div>

          <div className="p-field mb-0 report-config-modal__column-field">
            <label
              htmlFor="report-config-contribution-plan-groups"
              className="report-config-modal__column-label"
            >
              <span className="labels">{t("reportConfigs.column_contribution_plan_groups")}</span>
            </label>
            <span className="report-config-modal__column-control">
              <MultiSelect
                id="report-config-contribution-plan-groups"
                value={selectedContributionPlanGroups}
                options={contributionPlanGroupOptions}
                optionLabel="name"
                dataKey="id"
                placeholder={t("reportConfigs.filter_plan_groups_placeholder")}
                filter
                showClear
                display="chip"
                disabled={fetchingOptions || !planGroups.length}
                panelClassName="report-config-multiselect-panel"
                className={classNames("p-column-filter report-config-column-field", {
                  "p-invalid": columnsInvalid || planGroupsOverlapInvalid,
                })}
                onChange={(e) =>
                  onFormChange({
                    contributionPlanGroupIds: (e.value || []).map((group) => group.id),
                  })
                }
              />
            </span>
            {columnsInvalid && (
              <small className="p-error">{t("reportConfigs.columns_required")}</small>
            )}
          </div>

          {submitError && (
            <Message
              severity="error"
              text={t("reportConfigs.create_error")}
              className="w-full mt-3"
            />
          )}
          </section>
        </div>
      </form>
    </Dialog>
  );
}

export default CreateReportConfigModal;
